import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../contexts/AuthContext';
import { Member } from '../types';
import { useUsers, useCreateUser, useUpdateUser, useUploadUserPhoto, useClientTags, useOrganizationalRoles, useIsAdmin, useIsManager, useMyDirectReports, useUnassignedGustoUsers, useGustoEnabled } from '../hooks';
import { Button } from '../components/ui/button';
import { Alert, AlertDescription } from '../components/ui/alert';
import { PageHeader } from '../components/page-header';
import { Card } from '../components/ui/card';
import { PeopleTable, PersonFormDialog, PeopleFilters, DeactivatePersonDialog } from '../components/people';
import { Plus, Eye, EyeOff, X } from 'lucide-react';
import { dateToISOString } from '../utils/formatters';

interface PersonFormData {
  name: string;
  email: string;
  reportsTo: string;
  organizationRole: string;
  clients: string[];
  birthDate: string;
  hireDate: string;
  contractRate: string;
  timezone: string;
  offboardingDate: string;
  offboardingReason: string;
}

const People: React.FC = () => {
  const { t } = useTranslation('people');
  const { t: tCommon } = useTranslation('common');
  useAuth();
  const navigate = useNavigate();
  const [showInactive, setShowInactive] = useState(false);
  const [openDialog, setOpenDialog] = useState(false);
  const [editingPerson, setEditingPerson] = useState<Member | null>(null);
  const [deactivatingPerson, setDeactivatingPerson] = useState<Member | null>(null);
  const [error, setError] = useState<string | null>(null);

  // Filter state
  const [searchQuery, setSearchQuery] = useState('');
  const [roleFilter, setRoleFilter] = useState('__all__');
  const [reportsToFilter, setReportsToFilter] = useState('__all__');
  const [clientsFilter, setClientsFilter] = useState<string[]>([]);

  // Hooks
  const { data: allPeople = [], isLoading: isLoadingAll, refetch: refetchAll } = useUsers(showInactive);
  const { data: directReports = [], isLoading: isLoadingDirectReports, refetch: refetchDirectReports } = useMyDirectReports();
  const { data: allClientTags = [] } = useClientTags();
  const { data: organizationalRoles = [] } = useOrganizationalRoles(false);
  const createUser = useCreateUser();
  const updateUser = useUpdateUser();
  const uploadPhoto = useUploadUserPhoto();

  // Check for admin or manager role
  const { isAdmin, isLoading: adminLoading } = useIsAdmin();
  const { isManager, isLoading: managerLoading } = useIsManager();
  const hasAccess = isAdmin || isManager;

  const { isConnected: gustoConnected } = useGustoEnabled();
  const { data: unassignedGustoUsers } = useUnassignedGustoUsers(hasAccess && gustoConnected);
  const unassignedCount = unassignedGustoUsers?.length ?? 0;
  const [bannerDismissed, setBannerDismissed] = useState(false);

  // Scope people list: admins see all, managers see only their direct reports
  const people = isAdmin ? allPeople : directReports;
  const isLoading = isAdmin ? isLoadingAll : isLoadingDirectReports;

  // Filter and sort people
  const filteredAndSortedPeople = useMemo(() => {
    let filtered = [...people];

    // Filter by search query (name)
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase().trim();
      filtered = filtered.filter(person => 
        person.name.toLowerCase().includes(query)
      );
    }

    // Filter by role
    if (roleFilter !== '__all__') {
      filtered = filtered.filter(person => 
        person.organizationRole === roleFilter
      );
    }

    // Filter by reports to
    if (reportsToFilter === '__none__') {
      filtered = filtered.filter(person => !person.reportsTo);
    } else if (reportsToFilter !== '__all__') {
      filtered = filtered.filter(person => 
        person.reportsTo?.id === reportsToFilter
      );
    }

    // Filter by clients (OR logic - match any selected client)
    if (clientsFilter.length > 0) {
      filtered = filtered.filter(person => 
        person.clients && person.clients.some(client => clientsFilter.includes(client))
      );
    }

    // Sort by name
    return filtered.sort((a, b) => a.name.localeCompare(b.name));
  }, [people, searchQuery, roleFilter, reportsToFilter, clientsFilter]);

  const handleClearFilters = () => {
    setSearchQuery('');
    setRoleFilter('__all__');
    setReportsToFilter('__all__');
    setClientsFilter([]);
  };

  useEffect(() => {
    if (!adminLoading && !managerLoading && !hasAccess) {
      navigate('/dashboard');
    }
  }, [hasAccess, adminLoading, managerLoading, navigate]);

  const handleOpenDialog = (person?: Member) => {
    setEditingPerson(person || null);
    setOpenDialog(true);
    setError(null);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setEditingPerson(null);
    setError(null);
  };

  const handleSubmit = async (formData: PersonFormData, photo: File | null) => {
    const payload: Record<string, unknown> = {
      name: formData.name,
      organizationRole: formData.organizationRole,
      reportsToId: formData.reportsTo === '__none__' ? null : formData.reportsTo,
      clients: formData.clients && formData.clients.length > 0 ? formData.clients : [],
      birthDate: formData.birthDate ? dateToISOString(formData.birthDate) : null,
      hireDate: formData.hireDate ? dateToISOString(formData.hireDate) : null,
      contractRate: formData.contractRate ? parseFloat(formData.contractRate) : null,
      timezone: formData.timezone || null,
    };

    // Off-boarding details are only editable while the member is inactive.
    if (editingPerson && editingPerson.isActive === false) {
      if (formData.offboardingDate) {
        payload.offboardingDate = dateToISOString(formData.offboardingDate);
      }
      // Always send the reason: a blank value clears the stored one.
      payload.offboardingReason = formData.offboardingReason;
    }

    // Only include email if creating new member or email has changed
    if (!editingPerson || formData.email !== editingPerson.email) {
      payload.email = formData.email;
    }

    let userId: string;
    if (editingPerson) {
      await updateUser.mutateAsync({ id: editingPerson.id, data: payload as any });
      userId = editingPerson.id;
    } else {
      const newUser = await createUser.mutateAsync(payload as any);
      userId = newUser.id;
    }

    // Upload photo if provided
    if (photo) {
      await uploadPhoto.mutateAsync({ id: userId, file: photo });
    }

    handleCloseDialog();
    if (isAdmin) {
      refetchAll();
    } else {
      refetchDirectReports();
    }
  };

  const handleDelete = (id: string) => {
    const person =
      allPeople.find((p) => p.id === id) ||
      directReports.find((p) => p.id === id) ||
      ({ id, name: '' } as Member);
    setDeactivatingPerson(person);
  };

  const handleConfirmDeactivate = async (
    offboardingDate: string,
    offboardingReason: string
  ) => {
    if (!deactivatingPerson) return;
    // Rejections propagate to the dialog, which shows the backend message inline.
    await updateUser.mutateAsync({
      id: deactivatingPerson.id,
      data: {
        isActive: false,
        offboardingDate,
        offboardingReason: offboardingReason || null,
      },
    });
    setDeactivatingPerson(null);
    if (isAdmin) {
      refetchAll();
    } else {
      refetchDirectReports();
    }
  };

  const handleReactivate = async (id: string) => {
    if (!window.confirm(t('reactivateConfirm', 'Are you sure you want to reactivate this person? They will be able to log in again.'))) {
      return;
    }

    try {
      await updateUser.mutateAsync({ id, data: { isActive: true } });
      if (isAdmin) {
        refetchAll();
      } else {
        refetchDirectReports();
      }
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to reactivate person');
    }
  };

  const getPersonName = (personId?: string) => {
    if (!personId) return tCommon('none');
    const person = allPeople.find((p) => p.id === personId);
    return person ? person.name : tCommon('unknown');
  };

  if (adminLoading || managerLoading || !hasAccess) {
    return null;
  }

  return (
    <div data-testid="people-page">
      <PageHeader
        title={isManager ? t('myTeamTitle', 'My Team') : t('title')}
        description={isManager ? t('myTeamDescription', 'View and manage your direct reports') : t('description')}
        testId="people-header"
      >
        {isAdmin && (
          <Button
            variant={showInactive ? "default" : "outline"}
            onClick={() => setShowInactive(!showInactive)}
            className="gap-2"
            data-testid="people-button-toggle-inactive"
          >
            {showInactive ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
            {showInactive ? t('hideInactive') : t('showInactive')}
          </Button>
        )}
        {isAdmin && (
          <Button
            onClick={() => handleOpenDialog()}
            className="gap-2"
            data-testid="people-button-add-person"
          >
            <Plus className="h-4 w-4" />
            {t('addPerson')}
          </Button>
        )}
      </PageHeader>

      {hasAccess && unassignedCount > 0 && !bannerDismissed && (
        <Alert className="mb-4 border-amber-300 bg-amber-50" data-testid="gusto-unassigned-banner">
          <div className="flex items-center justify-between">
            <AlertDescription className="text-amber-800">
              {t('gustoUnassigned.banner', { count: unassignedCount })}
            </AlertDescription>
            <button
              onClick={() => setBannerDismissed(true)}
              className="ml-4 text-amber-600 hover:text-amber-800"
              data-testid="gusto-unassigned-banner-dismiss"
              aria-label={tCommon('buttons.close')}
            >
              <X className="h-4 w-4" />
            </button>
          </div>
        </Alert>
      )}

      {error && (
        <Alert variant="destructive" className="mb-4" data-testid="people-error-alert">
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}

      <Card className="p-6" data-testid="people-table-card">
        <PeopleFilters
          searchQuery={searchQuery}
          roleFilter={roleFilter}
          reportsToFilter={reportsToFilter}
          clientsFilter={clientsFilter}
          onSearchChange={setSearchQuery}
          onRoleChange={setRoleFilter}
          onReportsToChange={setReportsToFilter}
          onClientsChange={setClientsFilter}
          onClearFilters={handleClearFilters}
          people={people}
          organizationalRoles={organizationalRoles}
          allClientTags={allClientTags}
          filteredCount={filteredAndSortedPeople.length}
          totalCount={people.length}
        />
        <PeopleTable
          people={filteredAndSortedPeople}
          loading={isLoading}
          onDelete={handleDelete}
          onReactivate={handleReactivate}
          getPersonName={getPersonName}
        />
      </Card>

      <PersonFormDialog
        open={openDialog}
        onClose={handleCloseDialog}
        onSubmit={handleSubmit}
        editingPerson={editingPerson}
        people={allPeople}
        allClientTags={allClientTags}
      />

      <DeactivatePersonDialog
        open={!!deactivatingPerson}
        personName={deactivatingPerson?.name || ''}
        onClose={() => setDeactivatingPerson(null)}
        onConfirm={handleConfirmDeactivate}
      />
    </div>
  );
};

export default People;
